import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { CourseMigrationService } from '@/services/CourseMigrationService';
import { Loader2, Database, Upload } from 'lucide-react';

const CoursesMigrationTrigger: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [migrationStats, setMigrationStats] = useState<{
    successful: number;
    failed: number;
    errors: string[];
  } | null>(null);

  const handleMigration = async () => {
    setIsLoading(true);
    setMigrationStats(null);
    
    try {
      toast.info('Starting course migration to database...');
      
      const result = await CourseMigrationService.migrateAllCourses();
      
      if (result.successful > 0) {
        toast.success(`Migration completed! ${result.successful} courses migrated successfully.`);
        setMigrationStats({
          successful: result.successful,
          failed: result.failed,
          errors: result.errors
        });
      } else {
        toast.error(`Migration failed: ${result.errors[0] || 'Unknown error'}`);
        setMigrationStats({
          successful: result.successful,
          failed: result.failed,
          errors: result.errors
        });
      }
    } catch (error) {
      console.error('Migration trigger error:', error);
      toast.error('Failed to start migration');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Course Database Migration
        </CardTitle>
        <CardDescription>
          Migrate all 120+ courses from the codebase to the database for unified access and management.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button 
          onClick={handleMigration} 
          disabled={isLoading}
          className="w-full"
          size="lg"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Migrating Courses...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Start Migration
            </>
          )}
        </Button>

        {migrationStats && (
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-green-600">Successful:</span>
              <span className="font-medium">{migrationStats.successful}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-red-600">Errors:</span>
              <span className="font-medium">{migrationStats.failed}</span>
            </div>
            
            {migrationStats.errors.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-medium text-red-600 mb-1">Errors:</p>
                <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto">
                  {migrationStats.errors.map((error, index) => (
                    <div key={index} className="mb-1">{error}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CoursesMigrationTrigger;